import type { CareTierId } from "@/data/tunables";

/**
 * v0.14 "The Event Card" (RFC §8a) — the choice-event content set, one
 * card per scene. Data-only (mirrors data/letters.ts); the scheduler
 * spawns by scene, the engine ages/expires, actions.answerCard applies
 * a chosen branch. Wording is Russell's to override; costs are the
 * shipped defaults (Control-Room-tunable via EVENTS.card).
 *
 * PASSIVE-ON-EXPIRE: `passive` auto-fires when the timer runs out — so
 * ignoring a card is a visible decision, never an ambush. CRISIS cards
 * additionally `bleed` per-tick while open (the only cards where waiting
 * is catastrophic — the delay-has-teeth property).
 */

/** Scenes a card can attach to (care tiers + the two non-tier scenes). */
export type CardScene = "office" | CareTierId | "alumni";
export const CARD_SCENES: readonly CardScene[] = [
  "office",
  "detox",
  "residential",
  "php",
  "iop",
  "soberLiving",
  "alumni",
] as const;

/** Compact effect vocabulary — resolved by systems/events.applyCardEffects. */
export type CardEffect =
  | { type: "funding"; amount: number } // signed
  | { type: "heat"; amount: number } // >= 0 (applyHeatGain)
  | { type: "dea"; amount: number } // >= 0
  | { type: "rep"; amount: number } // signed (negative = applyRepLoss magnitude)
  | { type: "sk"; amount: number } // >= 0 (account.streetKnowledge)
  | { type: "losePatient" } // a DEPARTURE (walk/discharge/eviction) — no death accounting
  | { type: "killPatient" } // a DEATH — removes a patient AND records deathsEver/deathsPending
  | { type: "roll"; chance: number; then: CardEffect[]; else: CardEffect[] };

export interface CardChoice {
  label: string;
  effects: CardEffect[];
}

export interface CardDef {
  id: string;
  scene: CardScene;
  crisis?: boolean;
  title: string;
  body: string;
  choices: CardChoice[]; // 2–3
  /** Auto-fires on timer expiry (the passive outcome). */
  passive: CardEffect[];
  /** CRISIS only: applied every LIVE tick the card stays open. */
  bleed?: CardEffect[];
}

export const CARDS: readonly CardDef[] = [
  {
    id: "office-review",
    scene: "office",
    title: "LOCAL WOMAN WARNS OTHERS",
    body: "“They cared more about my copay than my kid.” 340 likes and climbing.",
    choices: [
      { label: "Respond with compassion", effects: [{ type: "rep", amount: 4 }] },
      {
        label: "Bury it under fifty five-star testimonials",
        effects: [{ type: "funding", amount: -200 }, { type: "rep", amount: 10 }, { type: "roll", chance: 0.4, then: [{ type: "heat", amount: 6 }], else: [] }],
      },
      { label: "Report it as a “HIPAA violation”", effects: [{ type: "funding", amount: -20 }, { type: "rep", amount: 3 }, { type: "heat", amount: 2 }] },
    ],
    passive: [{ type: "rep", amount: -8 }],
  },
  {
    id: "detox-seizure",
    scene: "detox",
    crisis: true,
    title: "BED 3 IS SEIZING",
    body: "Withdrawal, and it is going wrong. Every second counts — someone should decide.",
    choices: [
      { label: "Call 911", effects: [{ type: "losePatient" }] },
      {
        label: "Manage it in-house",
        effects: [{ type: "roll", chance: 0.6, then: [{ type: "funding", amount: 150 }], else: [{ type: "killPatient" }, { type: "heat", amount: 12 }, { type: "dea", amount: 8 }] }],
      },
      { label: "“Upgrade them to our medical suite”", effects: [{ type: "funding", amount: 300 }, { type: "heat", amount: 6 }] },
    ],
    passive: [{ type: "killPatient" }, { type: "heat", amount: 15 }, { type: "dea", amount: 10 }],
    bleed: [{ type: "rep", amount: -0.5 }, { type: "heat", amount: 0.3 }],
  },
  {
    id: "residential-door",
    scene: "residential",
    title: "THE DOOR",
    body: "Bag packed, hand on the handle. They want to leave against advice.",
    choices: [
      { label: "Let them go", effects: [{ type: "losePatient" }] },
      { label: "Talk them down", effects: [{ type: "roll", chance: 0.5, then: [], else: [{ type: "losePatient" }] }] },
      { label: "“Therapeutic hold”", effects: [{ type: "heat", amount: 10 }, { type: "roll", chance: 0.3, then: [{ type: "heat", amount: 15 }, { type: "rep", amount: -10 }], else: [] }] },
    ],
    passive: [{ type: "losePatient" }],
  },
  {
    id: "php-audit",
    scene: "php",
    title: "AUDIT LETTER",
    body: "They would like to discuss why group therapy was billed as “individual crisis intervention.” 1,400 times.",
    choices: [
      { label: "Comply honestly", effects: [{ type: "funding", amount: -400 }] },
      { label: "“Reconcile” the records overnight", effects: [{ type: "funding", amount: -100 }, { type: "heat", amount: 8 }] },
      { label: "Lawyer up", effects: [{ type: "funding", amount: -250 }] },
    ],
    passive: [{ type: "funding", amount: -600 }, { type: "heat", amount: 4 }],
  },
  {
    id: "iop-ua",
    scene: "iop",
    title: "THE HOT UA",
    body: "The screen came back positive. Policy says one thing. Billing says another.",
    choices: [
      { label: "Discharge per policy", effects: [{ type: "losePatient" }] },
      { label: "Order a confirmatory re-test", effects: [{ type: "funding", amount: 120 }] },
      { label: "Note “expected metabolite variance”", effects: [{ type: "funding", amount: 40 }, { type: "roll", chance: 0.3, then: [{ type: "heat", amount: 8 }], else: [] }] },
    ],
    passive: [{ type: "losePatient" }],
  },
  {
    id: "sober-rent",
    scene: "soberLiving",
    title: "RENT IS DUE",
    body: "They can’t make it this month. The bed has a waiting list.",
    choices: [
      { label: "Evict", effects: [{ type: "losePatient" }] },
      { label: "Let them work it off", effects: [{ type: "rep", amount: 3 }] },
      { label: "Carry them quietly", effects: [{ type: "funding", amount: -80 }, { type: "rep", amount: 6 }] },
    ],
    passive: [{ type: "losePatient" }, { type: "rep", amount: -4 }],
  },
  {
    id: "alumni-relapse",
    scene: "alumni",
    title: "2 A.M. — A GRADUATE CALLS",
    body: "They’re using again. They’re asking if they can come back.",
    choices: [
      { label: "Readmit them", effects: [{ type: "funding", amount: 250 }] },
      { label: "Refer them elsewhere", effects: [{ type: "rep", amount: 6 }] },
      { label: "“You’ve got this, champ” — hang up", effects: [] },
    ],
    passive: [{ type: "rep", amount: -2 }],
  },
] as const;

export function cardById(id: string): CardDef {
  const c = CARDS.find((d) => d.id === id);
  if (!c) throw new Error(`cardById: unknown card id "${id}"`);
  return c;
}

/** Cards authored for a scene (the scheduler's per-scene pool). */
export function cardsForScene(scene: CardScene): CardDef[] {
  return CARDS.filter((c) => c.scene === scene);
}
